/**
 * Um cache por curso.
 *
 * Enquanto trocar de curso reemitia o token, o AuthContext descartava o cache
 * inteiro e chaves como `['modules']` bastavam. Agora a troca é só navegação e o
 * cache sobrevive — a mesma chave passaria a servir os módulos do curso anterior.
 *
 * A separação podia ser feita de três formas, e vale registrar por que esta:
 *
 *  - escrever o curso em cada uma das ~100 chaves espalhadas pelas telas: é a
 *    mesma regra repetida cem vezes, e basta esquecer uma para vazar dado de um
 *    curso no outro, em silêncio;
 *  - um `queryKeyHashFn` que injeta o curso no hash: parece resolver tudo num
 *    lugar só, mas o hash passa a depender de estado externo. Foi tentado e
 *    quebrou: entre o registro de uma query e a observação dela o curso pode
 *    mudar, e aí o observer procura por um hash que não existe — a tela fica
 *    carregando para sempre e a requisição repete em laço;
 *  - um QueryClient por curso, que é o que está aqui. Nenhuma chave muda,
 *    nenhuma função vira impura, e o isolamento é estrutural: caches diferentes
 *    não têm como se enxergar.
 *
 * Os clients são memoizados por curso, então voltar para um curso já visitado
 * reaproveita o que ele tinha carregado.
 */

import { QueryClient } from '@tanstack/react-query';

const DEFAULTS = {
  queries: {
    staleTime: 1000 * 60 * 5, // 5 minutos - dados considerados frescos
    gcTime: 1000 * 60 * 10, // 10 minutos - cache time (anteriormente cacheTime)
    refetchOnWindowFocus: false, // Não refazer requisição ao focar na janela
    retry: 1, // Tentar novamente 1 vez em caso de erro
  },
};

export function createQueryClient(): QueryClient {
  return new QueryClient({ defaultOptions: DEFAULTS });
}

/** Client de cada curso, vivo enquanto a aba estiver aberta. */
const porCurso = new Map<number, QueryClient>();

export function queryClientForCourse(topicId: number): QueryClient {
  const existente = porCurso.get(topicId);
  if (existente) return existente;

  const novo = createQueryClient();
  porCurso.set(topicId, novo);
  return novo;
}

/**
 * Descarta o cache de todos os cursos.
 *
 * Chamado no fim da sessão: o cache é por pessoa, e entrar com outra conta na
 * mesma aba não pode servir dados da anterior. Sem isto, o `clear()` do
 * AuthContext limparia só o client da vitrine.
 */
export function clearAllCourseCaches(): void {
  for (const client of porCurso.values()) {
    client.clear();
  }
  porCurso.clear();
}
